import moment from 'moment'

import type { IPostView } from '../../post/post.service'

import { app } from '../../app.module'

interface IPostCard extends IPostView {
  title: string
  message: string
  type: string
  hashtags: [string]
  plays: number
  views: number
  dislikes: number
  duration: number
  retweets: number
  shares: number
  collects: number
  engagements: number
  engagementRate: number
  music: {
    id: string
    title: string
    authorName: string
    thumbnailUrl: string
    url: string
  }
}
interface IPostCardView extends IPostCard {
  engagementRateView: string
}

class PostCardComponentCtrl {
  private snsType: string
  private postId: number
  private postData: IPostCard
  private postDataView: IPostCardView

  constructor(
    private EditPostTagModalService,
    private CsvDownloadCommon
  ) {}

  /**
   * 初期化
   */
  public $onInit(): void {
    switch (this.snsType) {
      case 'facebook':
        // FBの投稿を取得
        this.EditPostTagModalService.Init.getPostById(this.snsType, this.postId).then((fbPost) => {
          this.setBasicPostData(fbPost)
          this.postData.account.accountUrl = 'https://www.facebook.com/' + fbPost.accountId
          this.postData.likes = fbPost.likes
          this.postData.comments = fbPost.comments
          this.postData.shares = fbPost.shares
          this.postData.engagements = fbPost.engagements
          this.postData.engagementRate = (fbPost.engagements / fbPost.accountFollowers) * 100
          this.postData.views = fbPost.views
          this.setPostDataView()
        })
        break

      case 'twitter':
        // TWの投稿を取得
        this.EditPostTagModalService.Init.getPostById(this.snsType, this.postId).then((twPost) => {
          this.setBasicPostData(twPost)
          this.postData.account.accountUrl = 'https://x.com/' + twPost.accountUsername
          this.postData.likes = twPost.likes
          this.postData.retweets = twPost.retweets
          this.postData.engagements = twPost.engagements
          this.postData.engagementRate = (twPost.engagements / twPost.accountFollowers) * 100
          this.setPostDataView()
        })
        break

      case 'instagram':
        // Inの投稿を取得
        this.EditPostTagModalService.Init.getPostById(this.snsType, this.postId).then((inPost) => {
          this.setBasicPostData(inPost)
          this.postData.account.accountUrl = 'https://www.instagram.com/' + inPost.accountUsername
          this.postData.likes = inPost.likes
          this.postData.comments = inPost.comments
          this.postData.engagements = inPost.engagements
          this.postData.engagementRate = (inPost.engagements / inPost.accountFollowers) * 100
          this.postData.views = inPost.views
          this.setPostDataView()
        })
        break

      case 'youtube':
        // Ytの投稿を取得
        this.EditPostTagModalService.Init.getPostById(this.snsType, this.postId).then((ytPost) => {
          this.setBasicPostData(ytPost)
          this.postData.account.accountUrl = 'https://www.youtube.com/channel/' + ytPost.accountId
          this.postData.title = ytPost.title
          this.postData.views = ytPost.views
          this.postData.likes = ytPost.likes
          this.postData.dislikes = ytPost.dislikes
          this.postData.comments = ytPost.comments
          this.postData.duration = ytPost.duration
          this.setPostDataView()
        })
        break

      case 'line':
        // Lineの投稿を取得
        this.EditPostTagModalService.Init.getPostById(this.snsType, this.postId).then(
          (linePost) => {
            this.setBasicPostData(linePost)
            this.postData.account.accountUrl = 'https://timeline.line.me/post/' + linePost.accountId
            this.postData.likes = linePost.likes
            this.postData.comments = linePost.comments
            this.postData.shares = linePost.shares
            this.postData.engagements = linePost.engagements
            this.postData.engagementRate = (linePost.engagements / linePost.accountFollowers) * 100
            this.setPostDataView()
          }
        )
        break

      case 'tiktok':
        // TikTokの投稿を取得
        this.EditPostTagModalService.Init.getPostById(this.snsType, this.postId).then((ttPost) => {
          this.setBasicPostData(ttPost)
          this.postData.account.accountUrl = 'https://www.tiktok.com/@' + ttPost.accountUsername
          this.postData.plays = ttPost.plays
          this.postData.likes = ttPost.likes
          this.postData.comments = ttPost.comments
          this.postData.shares = ttPost.shares
          this.postData.collects = ttPost.collects
          this.postData.engagements = ttPost.engagements
          this.postData.engagementRate = (ttPost.engagements / ttPost.plays) * 100
          this.postData.music = {
            id: ttPost.ttPostMusicId,
            title: ttPost.musicTitle,
            authorName: ttPost.musicAuthorName,
            thumbnailUrl: ttPost.musicThumbnailUrl,
            url: `https://www.tiktok.com/music/${ttPost.musicTitle}-${ttPost.ttPostMusicId}`,
          }
          this.setPostDataView()
        })
        break
    }
  }

  /**
   * 投稿の基本データをセット
   */
  public setBasicPostData(postData) {
    this.postData = {
      publishDate: moment(postData.publishDate).format('ll HH:mm'),
      message: postData.message,
      picture: [postData.thumbnailUrl],
      account: {
        screenName: postData.accountScreenName,
        pictureUrl: postData.accountPictureUrl,
        followers: postData.accountFollowers,
        categoryName: postData.accountCategoryName,
        subCategoryName: postData.accountSubCategoryName,
        accountUrl: null,
        accountId: null,
        snsType: null,
        dataFlg: null,
        hiddenSubscribersFlg: null,
        postDateFollowers: null,
      },
      hashtags: null,
      postId: null,
      postUrl: null,
      publishDateUnix: null,
      plays: null,
      likes: null,
      comments: null,
      tags: null,
      memoCount: null,
      title: null,
      views: null,
      dislikes: null,
      duration: null,
      retweets: null,
      shares: null,
      collects: null,
      engagements: null,
      engagementRate: null,
      type: this.CsvDownloadCommon.convertPostType(postData.type),
      music: null,
    }

    if (this.snsType !== 'facebook' && this.snsType !== 'line') {
      // ハッシュタグが存在する場合は、配列としてセットする
      if (postData.hashtagName !== null && postData.hashtagName !== undefined) {
        this.postData.hashtags = postData.hashtagName.split(',')
      } else {
        this.postData.hashtags = null
      }
    }
  }

  /**
   * 投稿表示用のデータをセット
   */
  private setPostDataView() {
    const engagementTarget = [
      'plays',
      'likes',
      'comments',
      'shares',
      'collects',
      'engagements',
      'engagementRate',
      'views',
      'retweets',
      'dislikes',
      'duration',
    ]

    this.postDataView = JSON.parse(JSON.stringify(this.postData))

    engagementTarget.forEach((target) => {
      if (this.postData[target]) {
        // 反応率の場合、数を固定小数点表記を用いてフォーマット
        if (target === 'engagementRate') {
          this.postDataView.engagementRateView = this.postData[target].toFixed(2)
        } else {
          // 反応率以外の場合、toLocaleString()で桁区切りを追加 ex. 1111 > 1,111
          this.postDataView[target] = this.postData[target].toLocaleString()
        }
      }
    })
  }
}

app.component('postCard', {
  bindings: {
    snsType: '@',
    postId: '<',
    postDataView: '=',
  },
  template: require('./post-card.pug')(),
  controller: PostCardComponentCtrl,
})
